import {Box, Typography, TypographyProps} from '@mui/material';
import {useMemo} from 'react';

import {WhatsNewCard} from 'modules/decisionsNext/pages/WhatsNewPage/sections/UpdatesAndNewFeaturesSection/types';

import {IS_DEV_ENV} from 'config';
import {localStorageKey} from 'core/constants';
import {FeatureFlags} from 'core/constants/featureFlags';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {useCheckEntityAccess} from 'core/hooks/useEntityAccess';
import {PageType} from 'modules/decisionsNext/constants/pageType';
import {FeatureType} from 'modules/decisionsNext/sections/WhatsNewSection/constants';
import {checkIsPeriodMatch} from 'modules/decisionsNext/sections/WhatsNewSection/utils';
import {useWhatsNewFiltersStore} from 'modules/decisionsNext/sections/WhatsNewSection/whatsNewPageStore';

const DescriptionTypo = (props: TypographyProps) => {
  return <Typography {...props} variant='body1' maxWidth={1000} />;
};

export function useWhatsNewData() {
  const checkAccess = useCheckEntityAccess();
  const {categories, period, types} = useWhatsNewFiltersStore(store => ({
    categories: store.filter.categories,
    period: store.filter.period,
    types: store.filter.types,
  }));
  const withAnnualizedValue = useFeatureFlag(FeatureFlags.AnnualizedValues);
  const withPolicyStatusFilter = useFeatureFlag(FeatureFlags.PolicyStatusFilter);

  const items: WhatsNewCard[] = useMemo(() => {
    const localData = IS_DEV_ENV && localStorage.getItem(localStorageKey.WhatsNewPageData);
    const testData: WhatsNewCard[] | undefined = localData ? JSON.parse(localData) : undefined;
    const withGlobalFilter = checkAccess('withGlobalFilter');
    const withReportsStudio = checkAccess('withReportsStudio');
    const withMonolineRatios = checkAccess('withMonolineRatios');
    const withKpis = checkAccess('withKpis');
    const withDocumentsCompare = checkAccess('withDocumentsCompare');
    const withProposalGeneration = checkAccess('withProposalGeneration');

    const rowItems: WhatsNewCard[] = [
      {
        title: 'Proposal Generation (Beta) — Limited Time Access',
        description: (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Typography variant='body1'>Proposal Generation is now available in beta for a limited time.</Typography>
            <Typography variant='body1Bold'>Build proposals faster</Typography>
            <Typography variant='body1'>
              Turn source insurance documents into a client ready proposal in minutes.
            </Typography>
            <Typography variant='body1Bold'>Upload 2 to 4 documents</Typography>
            <Typography variant='body1'>
              Add at least 2 and no more than 4 supporting documents to generate your proposal.
            </Typography>
            <Typography variant='body1Bold'>Review, preview, and finalize</Typography>
            <Typography variant='body1'>
              Review extracted values, preview the proposal as a PDF, and finalize when it is ready for client review.
            </Typography>
            <Typography variant='body1Bold'>Beta access is limited</Typography>
            <Typography variant='body1'>
              This release is available for a limited time while beta access is open.
            </Typography>
          </Box>
        ),
        featureType: FeatureType.NewFeature,
        categories: ['Proposal Generation'],
        date: new Date('04-28-2026'),
        isNew: true,
        video: {
          src: 'https://player.vimeo.com/video/1185594841?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'Proposal Generation (Beta)',
          paddingTop: '56.25%',
        },
        isVisible: withProposalGeneration,
      },
      {
        title: 'KPI Page - New Features April 2026',
        description: (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Typography variant='body1'>We&apos;ve updated the KPI page with several new features</Typography>
            <Typography variant='body1Bold'>Custom Card Names</Typography>
            <Typography variant='body1'>
              You can now rename KPI cards and replace default names with your own.
            </Typography>
            <Typography variant='body1Bold'>Filtering by Policy Status</Typography>
            <Typography variant='body1'>
              Policy KPIs now support filtering by status. Select one or more statuses to build metrics based on exactly
              the data you need.
            </Typography>
            <Typography variant='body1Bold'>Retention Metrics</Typography>
            <Typography variant='body1'>
              New Retention metrics are now available for Accounts, Policies, Premium, and Revenue.
            </Typography>
          </Box>
        ),
        featureType: FeatureType.Update,
        categories: [PageType.Kpi],
        date: new Date('04-14-2026'),
        video: {
          src: 'https://player.vimeo.com/video/1182696179?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'KPIs April Updates',
          paddingTop: '56.25%',
        },
      },
      {
        title: 'Documents Compare',
        description:
          'Compare two or more documents side by side to quickly spot differences across coverage, schedules, forms, and exclusions. Choose from Policy Checking, Renewal Analysis, or Quote Comparison to match your workflow.',
        featureType: FeatureType.NewFeature,
        categories: [PageType.DocumentsCompare],
        date: new Date('03-26-2026'),
        video: {
          src: 'https://player.vimeo.com/video/1177403094?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'Documents Compare',
          paddingTop: '56.25%',
        },
        isVisible: withDocumentsCompare,
      },
      {
        title: 'Carrier Report Customization',
        description:
          'The Carrier Report now supports customization, including page sorting, section visibility, and configurable behavior for certain pages, charts, and sections.',
        featureType: FeatureType.Update,
        categories: ['Carrier Report'],
        date: new Date('03-12-2026'),
        video: {
          src: 'https://player.vimeo.com/video/1172584825?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'CarrierReport-2026',
          paddingTop: '56.25%',
        },
      },
      {
        title: 'Custom Offset for Relative Date Filters',
        description:
          'You can now generate reports for future or past periods with a custom offset in Report Studio. The new Offset (in days) option is available in the Filters section for Relative date filters and works with Next N days and Previous N days.',
        featureType: FeatureType.Light,
        categories: [PageType.ReportStudio],
        date: new Date('02-03-2026'),
      },
      {
        title: 'Biz Orgs Table on Carrier Appetite Page',
        description:
          'Carrier Appetite page now includes a new table showing all Biz Orgs connected to Carriers from the My Book tab. Get a clearer view of how your Business Orgs work with existing Carriers, including policy activity and related producers.',
        featureType: FeatureType.NewFeature,
        categories: [PageType.CarrierAppetite],
        date: new Date('01-21-2026'),
      },
      {
        title: 'Filter policies by AMS policy status codes',
        description: (
          <DescriptionTypo>
            You can now filter policies by AMS policy status codes. <br />
            This update lets you see exactly which policy status codes are included in each AMS status, giving you more
            transparency and control when analyzing and filtering policies.
          </DescriptionTypo>
        ),
        featureType: FeatureType.NewFeature,
        categories: ['Filters'],
        date: new Date('12-30-2025'),
        isVisible: withPolicyStatusFilter && (withGlobalFilter || withReportsStudio),
      },
      {
        title: 'New AMS policy statuses added',
        description: (
          <DescriptionTypo>
            We’ve expanded the list of AMS policy statuses with two new options: Lost and Other. <br />
            Lost includes policies that are mapped to the Lost policy status in AMS. <br />
            Other groups policies that are not mapped to any of the existing AMS policy statuses. <br />
            These additions help you better categorize policies and get a clearer picture of their current state.
          </DescriptionTypo>
        ),
        featureType: FeatureType.Update,
        categories: ['Filters'],
        date: new Date('12-30-2025'),
        isVisible: withPolicyStatusFilter && (withGlobalFilter || withReportsStudio),
      },
      {
        title: 'Overview Metrics Period Comparison',
        description:
          'Displays how current values compare to the previous period, including percentage change and raw numbers. Helps identify trends and performance shifts over time. You can also view the list of policies for each period.',
        featureType: FeatureType.NewFeature,
        categories: ['General'],
        date: new Date('12-03-2025'),
        video: {
          src: 'https://player.vimeo.com/video/1143024604?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'Overview Metrics',
          paddingTop: '56.25%',
        },
        isVisible: withGlobalFilter,
      },
      {
        title: 'Improved Saved Filters Experience',
        description:
          'We added a more intuitive filters dropdown so you can quickly select and manage your Saved filters right from the top bar',
        categories: ['Filters'],
        featureType: FeatureType.NewFeature,
        date: new Date('11-25-2025'),
        video: {
          src: 'https://player.vimeo.com/video/1140138709?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'Saved Filters Dropdown',
          paddingTop: '56.25%',
        },
        isVisible: withGlobalFilter,
      },
      {
        title: 'Policy level in Monoline Ratios',
        description:
          'Monoline Policy level has been added. You can now switch the monoline logic based on Accounts or Policies.',
        categories: [PageType.MonolineRatios],
        featureType: FeatureType.NewFeature,
        date: new Date('11-01-2025'),
        isVisible: withMonolineRatios,
      },
      {
        title: 'Annualized premium type',
        description: 'New premium type option added to the Date & Type section – view your data in an annualized way.',
        categories: ['Filters'],
        featureType: FeatureType.NewFeature,
        date: new Date('10-28-2025'),
        isVisible: withAnnualizedValue,
      },
      {
        title: 'Monoline Ratios on Markets pages',
        description:
          'Monoline Ratios chart is now available on the Markets pages. You can now analyze Monoline vs. Multiline ratios directly within market views.',
        categories: [PageType.Markets],
        featureType: FeatureType.NewFeature,
        date: new Date('10-07-2025'),
        isVisible: withMonolineRatios,
      },
      {
        title: 'KPIs page improvements',
        description:
          'You can now add up to 250 KPI cards per dashboard (50 per section). Performance has also been optimized with section-level virtualization, and Production Credit is now available on KPIs page.',
        categories: [PageType.Kpi],
        featureType: FeatureType.Update,
        date: new Date('09-30-2025'),
        isVisible: withKpis,
      },
      {
        title: 'Filtering by Policy Statuses',
        description:
          'You can now filter policies by one or multiple statuses using the new Policy Status option in the Date & Type section.',
        categories: ['Filters'],
        featureType: FeatureType.NewFeature,
        date: new Date('09-23-2025'),
        isVisible: withPolicyStatusFilter,
      },
      {
        title: 'Page selection & Active Filters in Carrier Report',
        description: (
          <DescriptionTypo>
            We’ve added a new Page Selection feature – you can now choose which pages to include in the report. There’s
            also a new Active Filters page that shows all filters applied to the data.
          </DescriptionTypo>
        ),
        featureType: FeatureType.NewFeature,
        categories: [PageType.Markets],
        date: new Date('09-20-2025'),
        video: {
          src: 'https://player.vimeo.com/video/1132148425?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'Carrier PDFs',
          paddingTop: '56.25%',
        },
        isVisible: checkAccess('withMarkets'),
      },
      {
        title: 'New way to add KPIs',
        description: 'You can now add KPIs, create dashboards, and sections more easily — all with a single button.',
        featureType: FeatureType.NewFeature,
        categories: [PageType.Kpi],
        date: new Date('09-20-2025'),
        video: {
          src: 'https://player.vimeo.com/video/1135155034?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'New way to add KPIs',
          paddingTop: '56.25%',
        },
        isVisible: withKpis,
      },
      {
        title: 'New way to move KPI cards between sections',
        description:
          'You can now quickly move KPI cards between sections, making it easier to organize your dashboards.',
        featureType: FeatureType.NewFeature,
        categories: [PageType.Kpi],
        date: new Date('09-20-2025'),
        video: {
          src: 'https://player.vimeo.com/video/1135155074?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          title: 'New Way to Move KPI Cards between Sections',
          paddingTop: '56.25%',
        },
        isVisible: withKpis,
      },
    ];

    const rawItems: WhatsNewCard[] = testData || rowItems;

    return rawItems.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)).filter(item => item.isVisible !== false);
  }, [checkAccess, withPolicyStatusFilter, withAnnualizedValue]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const isCategoryMatch = categories.length ? item.categories.some(cat => categories.includes(cat)) : true;
      const isTypeMatch = types.length ? types.includes(item.featureType) : true;
      const isPeriodMatch = checkIsPeriodMatch(item.date, period);

      return isCategoryMatch && isTypeMatch && isPeriodMatch;
    });
  }, [categories, items, period, types]);

  return {items: filteredItems, allItems: items};
}
